import React, { useEffect } from "react";

import { navigate } from "gatsby";
import { Link } from "@gatsbyjs/reach-router";

import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import useComponentVisible from "../../../../hooks/useComponentVisible";
import { roundNumber } from "../../../../utils/NumberHelpers";
import { storageSession } from "../../../../utils/StorageHandler";
import { getCurrentParamString } from "../../../../utils/urlParams";
import resolveWebView from "../../../../utils/webViewRedirect";
import Button, { ButtonColor } from "../../../shared/NewForms/Button";
import useSnackbar from "../../../shared/Snackbar/store";
import fadeIn from "../animations/fadeIn";
import { fetchCart } from "../api";
import AddedToCartFromListOverlay from "../components/AddedToCartFromListOverlay";
import ProductPrice from "../components/ProductPrice";
import RegulationPrice from "../components/RegulationPrice";
import { ShippingInfoModalActivator } from "../components/ShippingPriceListModal";
import useCartStore, {
    iShopwareCartPriceReference,
    lastVisitedStorePathKey,
} from "../ShoppingCart/store";
import { iShopwareProduct } from "../store";

const Container = styled.div`
    position: relative;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 2em;
    column-gap: 4em;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        grid-template-columns: repeat(2, 1fr);
    `}
    ${({ theme }) => theme.breakpoints.from.normal.css`
        grid-template-columns: repeat(3, 1fr);
    `}
`;

const GridProductItem = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;

    ${({ theme }) => theme.breakpoints.upTo.small.css`
       box-shadow: 1px 1px 7px ${theme.colors.grey.toString()};
    `}
    :hover .shop-product-title {
        color: ${({ theme }) => theme.colors.red.toString()};
        transition: 0.5s;
    }
`;

const ProductName = styled(Link)`
    width: 100%;
    margin-bottom: auto;
    font-weight: bold;
    cursor: pointer;
`;

const ImageContainer = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1em;
    cursor: pointer;
`;

const ProductImage = styled.img`
    height: 250px;
`;

const ProductDetails = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 20px;
`;

const ProductPriceContainer = styled.div`
    margin-top: 20px;
`;

interface ProductListProps {
    products: Array<iShopwareProduct>;
}

const ProductList = ({ products }: ProductListProps) => {
    const { notify } = useSnackbar();
    const {
        ref: componentVisibleRef,
        isVisible: isOverlayVisible,
        setIsVisible: setIsOverlayVisible,
    } = useComponentVisible(false);

    useEffect(() => {
        // We have to fetch the cart id
        void fetchCart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { isCartLoading, addProductToCart } = useCartStore(
        (state) => ({
            isCartLoading: state.loading,
            addProductToCart: state.addProductToCart,
        }),
        shallow
    );

    const addProduct = async (product: iShopwareProduct) => {
        if (!product) {
            return;
        }

        const updatedCart = await addProductToCart(product.id, 1);
        if (updatedCart && updatedCart.errors.length === 0) {
            setIsOverlayVisible(true);
        } else {
            void notify(
                "Fehler",
                "Der Artikel konnte nicht in den Warenkorb gelegt werden."
            );
        }
    };

    const setLasVisitedStoreFrontPath = () => {
        const sessionItem = storageSession.get(lastVisitedStorePathKey);
        if (sessionItem) {
            const scroll = window.scrollY;
            storageSession.set(
                lastVisitedStorePathKey,
                sessionItem + `&scroll=${scroll}`
            );
        }
    };

    const getProductImage = (product: iShopwareProduct) => {
        return product.cover?.media?.url;
    };

    return (
        <Container>
            <div ref={componentVisibleRef}>
                <AddedToCartFromListOverlay
                    isVisible={isOverlayVisible}
                    close={() => setIsOverlayVisible(false)}
                />
            </div>
            <Grid>
                {products.map((product) => (
                    <GridProductItem css={fadeIn} key={product.id}>
                        <ImageContainer
                            to={`/shop/artikel/${
                                product.id + getCurrentParamString()
                            }`}
                            onClick={() => {
                                setLasVisitedStoreFrontPath();
                            }}
                        >
                            <ProductImage
                                src={getProductImage(product)}
                                alt={product.cover?.media?.alt}
                            />
                        </ImageContainer>
                        <ProductName
                            className="shop-product-title"
                            to={`/shop/artikel/${
                                product.id + getCurrentParamString()
                            }`}
                            onClick={() => {
                                setLasVisitedStoreFrontPath();
                            }}
                        >
                            {product.name}
                        </ProductName>
                        <ProductDetails>
                            <PriceReference
                                referencePrice={
                                    product.calculatedCheapestPrice
                                        ?.referencePrice
                                }
                            />

                            <ShippingInfoModalActivator
                                hasFreeShipping={product.shippingFree}
                            />
                            {product.deliveryTime?.name && (
                                <div>
                                    Lieferzeit: {product.deliveryTime?.name}
                                </div>
                            )}
                            <ProductPriceContainer>
                                <ProductPrice
                                    price={product.calculatedCheapestPrice}
                                />
                            </ProductPriceContainer>
                            <RegulationPrice product={product} />
                        </ProductDetails>
                        <Button
                            fullWidth
                            disabled={isCartLoading}
                            color={ButtonColor.red}
                            onClick={(event: any) => {
                                event.stopPropagation();
                                void addProduct(product);
                            }}
                            type="button"
                        >
                            In den Warenkorb
                        </Button>
                    </GridProductItem>
                ))}
            </Grid>
        </Container>
    );
};

interface iPriceReferenceProps {
    referencePrice?: iShopwareCartPriceReference;
}

const PriceReference = ({ referencePrice }: iPriceReferenceProps) => {
    if (!referencePrice) return null;
    return (
        <div>
            {roundNumber(referencePrice.price)} € */
            {referencePrice.referenceUnit}
            &nbsp;
            {referencePrice.unitName}
        </div>
    );
};

export default ProductList;
